import {
  Component,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { environment } from '../../../../../environments/environment';
import { Section } from '../../models/section.model';
import { SectionType } from '../../models/section-type.enum';
import { sectionTitleKey } from '../../../../../../features/document-builder/data/section-title-keys';

import { NumberedListSection } from '../numbered-list-section/numbered-list-section';
import { BulletListSection } from '../bullet-list-section/bullet-list-section';
import { ParagraphSection } from '../paragraph-section/paragraph-section';
import { KpiCardSection } from '../kpi-card-section/kpi-card-section';
import { TableSection } from '../table-section/table-section';
import { RiskSection } from '../risk-section/risk-section';
import { SummaryTermsSection } from '../summary-terms-section/summary-terms-section';
import { IrsRatingSection } from '../irs-rating-section/irs-rating-section';
import { FinancialPerformanceSection } from '../financial-performance-section/financial-performance-section';

import {
  TABLE_LIBRARY
} from '../../../../../../features/document-builder/data/table-library';

import {
  FinancialData
} from '../../../financial-data/financial-data';

import {
  IncomeStatementTypeCreditProposal
} from '../../../income-statement-type-credit-proposal/income-statement-type-credit-proposal';

import {
  CreditProposalExtraType
} from '../../../credit-proposal-extra-type/credit-proposal-extra-type';

import {
  TermsParameters
} from '../../../terms-parameters/terms-parameters';

import {
  FinancialAnalysisDetails
} from '../../../financial-analysis-details/financial-analysis-details';

import {
  CraditService
} from '../../../shared/services/cradit.service';
import { BorrowPerposalReview } from "../../../borrow-perposal-review/borrow-perposal-review";
import { RepaymentPlan } from "../../../repayment-plan/repayment-plan";
import { CalculateRepaymentPlan } from "../../../calculate-repayment-plan/calculate-repayment-plan";
import { InstitutionalRatingBuilder } from "../../../institutional-rating-builder/institutional-rating-builder";
import { RecommendationsSection } from "../../../recommendations-section/recommendations-section";
import { Repayment } from "../../../disbursement-repayment/repayment/repayment";
import { DisbursementRepaymentSummary } from "../../../disbursement-repayment-summary/disbursement-repayment-summary";
import { CreditProposalDocuments } from '../../../credit-proposal-documents/credit-proposal-documents';

@Component({
  selector: 'app-section-renderer',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NumberedListSection,
    BulletListSection,
    ParagraphSection,
    KpiCardSection,
    TableSection,
    RiskSection,
    SummaryTermsSection,
    IrsRatingSection,
    FinancialPerformanceSection,
    FinancialData,
    IncomeStatementTypeCreditProposal,
    CreditProposalExtraType,
    TermsParameters,
    // Required for app-financial-analysis-details
    FinancialAnalysisDetails,
    BorrowPerposalReview,
    RepaymentPlan,
    CalculateRepaymentPlan,
    InstitutionalRatingBuilder,
    RecommendationsSection,
    Repayment,
    DisbursementRepaymentSummary,
    CreditProposalDocuments,
    TranslatePipe
],
  templateUrl: './section-renderer.html',
  styleUrl: './section-renderer.css',
})
export class SectionRenderer
  implements OnInit, OnDestroy {
  readonly sectionTitleKey = sectionTitleKey;


  @Input() section!: Section;
  @Input() isEditMode = false;

  /**
   * Lets a parent that already renders the section title (e.g. the Document
   * Builder page header) suppress this component's own duplicate heading.
   */
  @Input() showHeading = true;

  readonly SectionType = SectionType;

  crdtPrpslSeq: number | null = null;

  private proposalSequenceSubscription?: Subscription;
  private readonly warnedUnsupportedTypes = new Set<SectionType>();

  constructor(
    private creditService: CraditService
  ) {}

  ngOnInit(): void {
    this.proposalSequenceSubscription =
      this.creditService.crdtPrpslSeq$
        .subscribe((sequence) => {
          this.crdtPrpslSeq = sequence;
        });
  }

  ngOnDestroy(): void {
    this.proposalSequenceSubscription
      ?.unsubscribe();
  }

  get tableConfig() {
    return (
      TABLE_LIBRARY[
        this.sectionConfig['tableId'] as string
      ] ?? {
        columns: [],
        data: []
      }
    );
  }

  /**
   * Safe access to section.config — never throws if a section has none.
   * Kept as `any` (matching Section.config's own type) so it stays assignable
   * to whichever strongly-typed config interface each child input expects.
   */
  get sectionConfig(): any {
    return this.section?.config ?? {};
  }

  /**
   * Called from the ngSwitchDefault branch only, so it only ever runs for a
   * section type with no matching component. Always returns true (it drives
   * an *ngIf so the fallback UI still renders); logs once per type in dev.
   */
  onUnsupportedSectionEncountered(): boolean {
    const type = this.section?.type;

    if (type && !this.warnedUnsupportedTypes.has(type)) {
      this.warnedUnsupportedTypes.add(type);

      if (!environment.production) {
        console.warn(
          `SectionRenderer: no component is registered for section type "${type}" (section id "${this.section?.id}").`,
        );
      }
    }

    return true;
  }
}